import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { ReportDateItem } from '../../types/api';
import { formatReportDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Loader2, RefreshCw } from 'lucide-react';

interface ReportDateSelectorProps {
  userId: string;
  onReportChange: (reportDate: string | null, analyticsId: string | null, bureauCount: number | null) => void;
  selectedDate: string | null;
  onLoadingChange?: (isLoading: boolean) => void;
  refreshTrigger?: number; // Add this to trigger refresh from parent
  onRefresh?: () => void;
  isRefreshing?: boolean;
  rightActions?: React.ReactNode;
}

export function ReportDateSelector({
  userId,
  onReportChange,
  selectedDate,
  onLoadingChange,
  refreshTrigger,
  onRefresh,
  isRefreshing = false,
  rightActions,
}: ReportDateSelectorProps) {
  const [reports, setReports] = useState<ReportDateItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      if (onLoadingChange) onLoadingChange(true);
      setError(null);
      
      console.log('[ReportDateSelector] Fetching reports for userId:', userId);
      
      // Don't pass bureau filter - fetch all reports
      const response = await apiService.getReportDates(userId);
      
      console.log('[ReportDateSelector] Reports fetched:', response.reports?.length || 0);
      
      if (response.success && response.reports.length > 0) {
        setReports(response.reports);
        
        // Auto-select the latest report (always select latest on refresh)
        console.log('[ReportDateSelector] Auto-selecting latest report');
        const latestReport = response.reports[0];
        onReportChange(latestReport.reportDate, latestReport.analyticsId, latestReport.bureauCount);
      } else {
        setReports([]);
      }
    } catch (err) {
      console.error('Failed to fetch report dates:', err);
      setError('Failed to load report history');
    } finally {
      setIsLoading(false);
      if (onLoadingChange) onLoadingChange(false);
    }
  };

  useEffect(() => {
    // Only fetch if userId is present and we haven't initialized yet
    if (!userId || hasInitializedRef.current) {
      return;
    }

    // Mark as initialized immediately to prevent duplicate calls
    hasInitializedRef.current = true;
    userIdRef.current = userId;
    
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0 && hasInitializedRef.current) {
      console.log('[ReportDateSelector] Refresh triggered, will select latest report');
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleSelectChange = (value: string) => {
    if (value === 'latest' && reports.length > 0) {
      const latestReport = reports[0];
      onReportChange(latestReport.reportDate, latestReport.analyticsId, latestReport.bureauCount);
    } else {
      const selectedReport = reports.find(r => r.reportDate === value);
      if (selectedReport) {
        onReportChange(selectedReport.reportDate, selectedReport.analyticsId, selectedReport.bureauCount);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading report history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive mb-4">
        {error}
      </div>
    );
  }

  // Show minimal UI with refresh button if no reports
  if (reports.length === 0) {
    return (
      <div className="pb-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            No reports available
          </div>
          
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                onClick={onRefresh}
                variant="outline"
                className="min-h-[40px] px-6 py-2.5 flex items-center gap-2 border-gray-300 rounded-lg shadow-sm"
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh Report</span>
              </Button>
            )}
            {rightActions}
          </div>
        </div>
      </div>
    );
  }

  const currentValue = selectedDate || (reports.length > 0 ? reports[0].reportDate : '');
  const hasMultipleReports = reports.length > 1;

  return (
    <div className="pb-3">
      <div className="flex items-center gap-2 mb-2">
        {onRefresh && (
          <Button
            onClick={onRefresh}
            variant="outline"
            className="min-h-[40px] px-6 py-2.5 flex items-center gap-2 border-gray-300 rounded-lg shadow-sm"
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">Refresh Report</span>
          </Button>
        )}
        {rightActions}
      </div>
      <p className="text-sm font-semibold text-gray-900 mb-2">Report From:</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {hasMultipleReports ? (
            <select
              value={currentValue}
              onChange={(e) => handleSelectChange(e.target.value)}
              className="min-w-[320px] w-auto max-w-xl min-h-[40px] pl-4 pr-12 py-2.5 text-sm border border-gray-200 rounded-lg bg-white shadow-sm whitespace-nowrap appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 12px center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em'
              }}
            >
              {reports.map((report, index) => (
                <option key={report.analyticsId} value={report.reportDate}>
                  {formatReportDate(report.reportDate, index === 0)} - {report.bureauCount} Bureau{report.bureauCount !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
          ) : (
            <div className="min-w-[320px] w-auto max-w-xl min-h-[40px] px-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white shadow-sm flex items-center whitespace-nowrap">
              {formatReportDate(reports[0].reportDate, true)} - {reports[0].bureauCount} Bureau{reports[0].bureauCount !== 1 ? 's' : ''}
            </div>
          )}
          
          <p className="text-sm whitespace-nowrap">
            <span className="text-gray-500">Available Reports: </span>
            <span className="text-base font-semibold text-gray-900">{reports.length}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

